import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Table as BaseTable,
  Cell,
  type TableColumn,
  type TableRow,
} from '_common/components/Table';

import { useSelector, useDispatch, usePublicProfiles } from '_common/hooks';
import { openAndUpdateModal } from '_common/modals/ModalsSlice';
import {
  useAddPermissionMutation,
  useGetObjectQuery,
  useGetUserGroupsQuery,
  useRemovePermissionMutation,
} from 'App/redux/objectApi';

import { PermissionColumn, CollaboratorColumn, RolesColumn, RemoveColumn } from './Columns';

import { useOnboardingExplorerActive } from '_common/hooks/Onboarding';

export type TableProps = {
  objectId: ObjectId;
  objectType: ObjectType;
  recursive: boolean;
  showGranular?: boolean;
  maxHeight?: string;
  testId: string;
};

type RowData = Permission.Collaborator & { id: string };

const Table = ({
  objectId,
  objectType,
  recursive,
  showGranular = true,
  maxHeight,
  testId,
}: TableProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const userId = useSelector((state) => state.auth.userId);
  const actionsCompleted = useSelector((state) => state.onboarding.actionsCompleted);
  const onboardingExplorerIsActive = useOnboardingExplorerActive();
  const { data } = useGetObjectQuery(
    {
      objectId,
      objectType,
      onboarding: onboardingExplorerIsActive && actionsCompleted.explorer_share_finish,
    },
    { skip: !objectId || !objectType, refetchOnMountOrArgChange: true },
  );
  const userGroups = useGetUserGroupsQuery(data ? Object.keys(data.permissions['users']) : [], {
    skip: !data || Object.keys(data.permissions['users']).length === 0,
  });

  const currentUserGroups = useMemo(() => userGroups.data ?? {}, [userGroups]);

  const { profiles } = usePublicProfiles(
    Object.keys(data?.subject_permissions ?? {})?.filter(
      (id) => data?.subject_permissions[id].subject_type === 'user',
    ),
  );
  const publicGroups = useSelector((state) => state.public.groups.profiles);

  const [addPermission] = useAddPermissionMutation();
  const [removePermission] = useRemovePermissionMutation();

  const [columns, setColumns] = useState<TableColumn<RowData>[]>([]);

  useEffect(() => {
    const baseColumns: TableColumn<RowData>[] = [
      {
        id: 'name',
        accessorKey: 'name',
        minWidth: 240,
        header: intl.formatMessage({ id: 'global.name' }),
        cell: (info) => {
          const collaborator = info.row.original;

          return (
            <Cell testId={`${collaborator.collaboratorId}-name-column`}>
              <CollaboratorColumn
                collaboratorId={`${collaborator.collaboratorId}`}
                collaboratorType={collaborator.collaboratorType}
              />
            </Cell>
          );
        },
      },
      {
        id: 'roles',
        accessorKey: 'collaboratorId',
        enableSorting: false,
        size: 216,
        header: intl.formatMessage({ id: 'global.roles' }),
        cell: (info) => {
          const collaborator = info.row.original;

          return (
            <Cell testId={`${collaborator.collaboratorId}-roles-column`}>
              <RolesColumn
                objectId={objectId}
                collaborator={collaborator}
                openDeleteLastPermissionModal={handleOpenDeleteLastPermissionModal}
                isLastPermission={isLastPermission}
                recursive={recursive}
              />
            </Cell>
          );
        },
      },
    ];

    if (showGranular) {
      setColumns([
        ...baseColumns,
        {
          id: 'co_owner',
          accessorKey: 'permissions.owner.active',
          enableSorting: false,
          size: 96,
          header: 'Co-owner',
          cell: (info) => {
            const collaborator = info.row.original;

            return (
              <Cell testId={`${collaborator.collaboratorId}-co_Owner-column`}>
                <PermissionColumn
                  objectId={objectId}
                  permission={collaborator.permissions.owner}
                  collaborator={collaborator}
                  recursive={recursive}
                  onChange={handlePermissionEdit}
                />
              </Cell>
            );
          },
        },
        {
          id: 'access',
          accessorKey: 'permissions.access.active',
          enableSorting: false,
          size: 80,
          header: intl.formatMessage({ id: 'global.access' }),
          cell: (info) => {
            const collaborator = info.row.original;

            return (
              <Cell testId={`${collaborator.collaboratorId}-access-column`}>
                <PermissionColumn
                  objectId={objectId}
                  permission={collaborator.permissions.access}
                  collaborator={collaborator}
                  recursive={recursive}
                  onChange={handlePermissionEdit}
                />
              </Cell>
            );
          },
        },
        {
          id: 'approve',
          accessorKey: 'permissions.approve.active',
          enableSorting: false,
          size: 96,
          header: intl.formatMessage({ id: 'global.approve' }),
          cell: (info) => {
            const collaborator = info.row.original;
            return (
              <Cell testId={`${collaborator.collaboratorId}-approve-column`}>
                <PermissionColumn
                  objectId={objectId}
                  permission={collaborator.permissions.approve}
                  collaborator={collaborator}
                  recursive={recursive}
                  onChange={handlePermissionEdit}
                />
              </Cell>
            );
          },
        },
        {
          id: 'delete',
          accessorKey: 'permissions.delete.active',
          enableSorting: false,
          size: 88,
          header: intl.formatMessage({ id: 'global.delete' }),
          cell: (info) => {
            const collaborator = info.row.original;
            return (
              <Cell testId={`${collaborator.collaboratorId}-delete-column`}>
                <PermissionColumn
                  objectId={objectId}
                  permission={collaborator.permissions.delete}
                  collaborator={collaborator}
                  recursive={recursive}
                  onChange={handlePermissionEdit}
                />
              </Cell>
            );
          },
        },
        {
          id: 'edit',
          accessorKey: 'permissions.edit.active',
          enableSorting: false,
          size: 72,
          header: intl.formatMessage({ id: 'global.edit' }),
          cell: (info) => {
            const collaborator = info.row.original;

            return (
              <Cell testId={`${collaborator.collaboratorId}-edit-column`}>
                <PermissionColumn
                  objectId={objectId}
                  permission={collaborator.permissions.edit}
                  collaborator={collaborator}
                  recursive={recursive}
                  onChange={handlePermissionEdit}
                />
              </Cell>
            );
          },
        },
        {
          id: 'comment',
          accessorKey: 'permissions.comment.active',
          enableSorting: false,
          size: 96,
          header: intl.formatMessage({ id: 'global.comment' }),
          cell: (info) => {
            const collaborator = info.row.original;
            return (
              <Cell testId={`${collaborator.collaboratorId}-comment-column`}>
                <PermissionColumn
                  objectId={objectId}
                  permission={collaborator.permissions.comment}
                  collaborator={collaborator}
                  recursive={recursive}
                  onChange={handlePermissionEdit}
                />
              </Cell>
            );
          },
        },
        {
          id: 'add_permissions',
          accessorKey: 'permissions.add_permission.active',
          enableSorting: false,
          size: 120,
          header: intl.formatMessage({ id: 'ADD_PERMISSION' }),
          cell: (info) => {
            const collaborator = info.row.original;

            return (
              <Cell testId={`${collaborator.collaboratorId}-addPermissions-column`}>
                <PermissionColumn
                  objectId={objectId}
                  permission={collaborator.permissions.add_permission}
                  collaborator={collaborator}
                  recursive={recursive}
                  onChange={handlePermissionEdit}
                />
              </Cell>
            );
          },
        },
        {
          id: 'remove_permissions',
          accessorKey: 'permissions.remove_permission.active',
          enableSorting: false,
          size: 120,
          header: intl.formatMessage({ id: 'global.modals.removeButton' }),
          cell: (info) => {
            const collaborator = info.row.original;
            return (
              <Cell testId={`${collaborator.collaboratorId}-removePermissions-column`}>
                <PermissionColumn
                  objectId={objectId}
                  permission={collaborator.permissions.remove_permission}
                  collaborator={collaborator}
                  recursive={recursive}
                  onChange={handlePermissionEdit}
                />
              </Cell>
            );
          },
        },
      ]);
    } else {
      setColumns([...baseColumns]);
    }
  }, [showGranular, data, userId, currentUserGroups]);

  const collaborators: { [collaboratorId in string]: Permission.Collaborator } = useMemo(() => {
    const collaborators: { [collaboratorId in string]: Permission.Collaborator } = {};

    const hasPermission = (permission: Permission.SubjectPermission | undefined) => {
      if (permission) {
        const hasGranularPermission = permission.granular;
        const hasRolesPermission = permission.roles && permission.roles?.length > 0;
        const hasGroupsPermission = permission.groups
          ? Object.keys(permission.groups).length > 0
          : false;
        return hasGranularPermission || hasRolesPermission || hasGroupsPermission;
      }
      return false;
    };

    if (data?.subject_permissions) {
      const subject_permissions: Permission.SubjectPermissions = data.subject_permissions;
      const objectCollaborators = [data.owner, ...Object.keys(subject_permissions)];

      objectCollaborators.forEach((collaboratorId, i) => {
        const isOwner = data.owner === collaboratorId;
        const collaborator = isOwner ? undefined : subject_permissions[collaboratorId];
        const collaboratorPermissions: Permission.Collaborator['permissions'] = {
          owner: { code: 'owner', active: isOwner, locked: false, disabled: isOwner },
          access: { code: 'access', active: isOwner, locked: false, disabled: isOwner },
          approve: { code: 'approve', active: isOwner, locked: false, disabled: isOwner },
          delete: { code: 'delete', active: isOwner, locked: false, disabled: isOwner },
          edit: { code: 'edit', active: isOwner, locked: false, disabled: isOwner },
          comment: { code: 'comment', active: isOwner, locked: false, disabled: isOwner },
          add_permission: {
            code: 'add_permission',
            active: isOwner,
            locked: false,
            disabled: isOwner,
          },
          remove_permission: {
            code: 'remove_permission',
            active: isOwner,
            locked: false,
            disabled: isOwner,
          },
        };
        if (collaborator) {
          (
            Object.keys(collaboratorPermissions) as Array<keyof typeof collaboratorPermissions>
          ).forEach((permissionId) => {
            const permission = collaborator.permissions[permissionId];

            if (permission) {
              const hasRolesPermission = permission.roles && permission.roles?.length > 0;
              const hasGroupsPermission = permission.groups
                ? Object.keys(permission.groups).length > 0
                : false;

              if (hasPermission(permission)) {
                collaboratorPermissions[permissionId].active = true;
              }

              if (hasRolesPermission || hasGroupsPermission) {
                collaboratorPermissions[permissionId].locked = true;
              }
            }

            if (permissionId !== 'owner' && hasPermission(collaborator.permissions.owner)) {
              collaboratorPermissions[permissionId].active = true;
              collaboratorPermissions[permissionId].locked = false;
              collaboratorPermissions[permissionId].disabled = true;
            }
          });
          collaborators[collaboratorId] = {
            collaboratorId,
            collaboratorType: collaborator.subject_type,
            isOwner,
            name:
              collaborator.subject_type === 'user'
                ? profiles[collaboratorId]?.name
                : publicGroups[collaboratorId]?.name,
            permissions: collaboratorPermissions,
            order: i,
          };
        } else {
          if (isOwner) {
            collaborators[collaboratorId] = {
              collaboratorId,
              collaboratorType: 'user',
              isOwner,
              name: profiles[collaboratorId]?.name,
              permissions: collaboratorPermissions,
              order: objectCollaborators.length + 1,
            };
          }
        }
      });
    }
    return collaborators;
  }, [data, profiles, publicGroups]);

  //#region Modals
  const handleOpenDeleteLastPermissionModal = (
    collaboratorId: Permission.Collaborator['collaboratorId'],
    collaboratorType: Permission.Collaborator['collaboratorType'],
    code?: Permission.Code,
  ) => {
    const elementName = collaborators?.[collaboratorId]?.name;

    const getDescription = () => {
      if (collaboratorType === 'group') {
        return (
          <FormattedMessage
            values={{
              name: elementName,
              type: data?.type,
            }}
            id="global.modals.delete.lastPermission"
          />
        );
      }

      return (
        <>
          <div>
            <FormattedMessage
              values={{
                name: elementName,
                type: data?.type,
              }}
              id="global.modals.delete.lastPermission"
            />
          </div>
          <div style={{ fontSize: '1.5rem', marginTop: '2.5rem' }}>
            <b>
              <FormattedMessage id="global.modals.delete.note" />
            </b>
            <FormattedMessage
              values={{
                name: elementName,
                type: data?.type,
              }}
              id="global.modals.delete.noteText"
            />
          </div>
        </>
      );
    };

    dispatch(
      openAndUpdateModal({
        modal: 'ConfirmationModal',
        data: {
          title: 'global.modals.delete.title',
          titleValues: {
            name: elementName,
          },
          messageChildren: getDescription(),
          confirmButtonTextId: 'global.delete',
          confirmButtonType: 'danger',
          cancelButtonTextId: 'global.cancel',
          actionCode: 'removeLastPermission',
          actionValue: {
            object: data,
            data,
            element: collaboratorType === 'user' ? 'users' : 'groups',
            permission: code,
            formKey: collaboratorType,
            selectedRow: collaborators[collaboratorId],
            recursive,
          },
          cancelButtonShow: true,
        },
      }),
    );
  };

  const handleOpenRemoveRemoveModal = (
    collaboratorType: Permission.Collaborator['collaboratorType'],
    code: Permission.Code,
    id?: string,
  ) => {
    dispatch(
      openAndUpdateModal({
        modal: 'ConfirmationModal',
        data: {
          title:
            collaboratorType === 'user'
              ? 'REMOVE_CO_OWNER_PERMISSIONS'
              : 'REMOVE_GROUP_CO_OWNER_PERMISSION',
          titleValues: { groupName: id && publicGroups[id] && publicGroups[id].name },
          message:
            collaboratorType === 'user'
              ? 'REMOVING_CO_OWNER_WILL_NO_LONGER_BE_ABLE_TO_MANAGE_CONFIRM'
              : 'REMOVING_CO_OWNER_FROM_GROUP_WILL_NO_LONGER_BE_ABLE_TO_MANAGE_CONFIRM',
          messageValues: { groupName: id && publicGroups[id] && publicGroups[id].name },
          confirmButtonTextId: 'global.modals.removeButton',
          confirmButtonType: 'danger',
          cancelButtonTextId: 'global.cancel',
          actionCode: 'removePermission',
          actionValue: {
            permission: code,
            userId,
            element: collaboratorType === 'user' ? 'users' : 'groups',
            groupId: id,
            recursive,
          },
          cancelButtonShow: true,
        },
      }),
    );
  };

  const handleOpenRemoveOwnerModal = (
    collaboratorType: Permission.Collaborator['collaboratorType'],
    code: Permission.CollaboratorPermission,
    collaboratorId: Permission.Collaborator['collaboratorId'],
  ) => {
    const type = collaboratorType === 'user' ? { user: collaboratorId } : { group: collaboratorId };

    dispatch(
      openAndUpdateModal({
        modal: 'ConfirmationModal',
        data: {
          title:
            collaboratorType === 'user'
              ? 'REMOVE_CO_OWNER_PERMISSIONS'
              : 'REMOVE_GROUP_CO_OWNER_PERMISSION',
          titleValues: {
            groupName: collaborators?.[collaboratorId]?.name,
          },
          message:
            collaboratorType === 'user'
              ? 'REMOVING_CO_OWNER_WILL_NO_LONGER_BE_ABLE_TO_MANAGE_CONFIRM'
              : 'REMOVING_CO_OWNER_FROM_GROUP_WILL_NO_LONGER_BE_ABLE_TO_MANAGE_CONFIRM',
          messageValues: {
            groupName: collaborators?.[collaboratorId]?.name,
          },
          confirmButtonTextId: 'global.modals.removeButton',
          confirmButtonType: 'danger',
          cancelButtonTextId: 'global.cancel',
          actionCode: 'removePermission',
          actionValue: {
            userId,
            objectType: data?.type,
            objectId: objectId,
            ...type,
            code,
            recursive,
          },
          cancelButtonShow: true,
        },
      }),
    );
  };
  //#endregion

  const isLastPermission = (
    collaboratorId: Permission.Collaborator['collaboratorId'],
    collaboratorType: Permission.Collaborator['collaboratorType'],
    clearRoles?: boolean,
  ) => {
    const permissions = Object.keys(data?.subject_permissions[collaboratorId]?.permissions);
    const roles =
      data?.permissions.roles[collaboratorType === 'user' ? 'users' : 'groups']?.[collaboratorId];

    if (permissions.length === 1) {
      let isLastPermission = false;

      permissions.forEach((permissionId) => {
        const permission = data?.subject_permissions[collaboratorId].permissions[permissionId];

        if (permission.granular && !permission.roles && !permission.groups) {
          isLastPermission = true;
        } else if (!permission.granular && permission.roles && !permission.groups) {
          isLastPermission = true;
        } else if (!permission.granular && !permission.roles && permission.groups) {
          isLastPermission = true;
        } else {
          isLastPermission = false;
        }
      });

      return isLastPermission;
    } else {
      let isLastPermission = true;

      permissions.forEach((permissionId) => {
        const permission = data?.subject_permissions[collaboratorId].permissions[permissionId];

        if (
          permission.granular ||
          (permission.roles && permission.roles.length > 1 && !clearRoles) ||
          permission.groups
        ) {
          isLastPermission = false;
          return;
        }
      });

      if (!isLastPermission) {
        return false;
      } else {
        if ((roles && roles.length <= 1) || clearRoles) {
          return true;
        }
      }
    }

    return false;
  };

  const isLastRemovePermission = (collaboratorId: Permission.Collaborator['collaboratorId']) => {
    const groupPermissions = data?.permissions['groups'];

    if (!currentUserGroups[collaboratorId]) {
      return true;
    }

    let hasRemove = true;
    [collaboratorId].forEach((key) => {
      if (groupPermissions[key] && groupPermissions[key].includes('remove_permission')) {
        hasRemove = false;
      }
    });

    return hasRemove;
  };

  const isMyLastGroupWithPermission = (code: string) => {
    const groupPermissions = data?.permissions['groups'];

    let count = 0;
    if (currentUserGroups) {
      Object.entries(currentUserGroups).forEach(([key, value]) => {
        if (key === userId) {
          value.forEach((groupId) => {
            if (groupPermissions[groupId] && groupPermissions[groupId].includes(code)) {
              count += 1;
            }
          });
        }
      });
    }

    return count === 1;
  };

  const handlePermissionEdit = (
    code: Permission.CollaboratorPermission,
    collaboratorId: Permission.Collaborator['collaboratorId'],
    collaboratorType: Permission.Collaborator['collaboratorType'],
  ) => {
    if (data) {
      const permissionsAux = { ...collaborators[collaboratorId].permissions };

      if (collaboratorType === 'user') {
        if (permissionsAux[code].active) {
          if (isLastPermission(collaboratorId, collaboratorType)) {
            handleOpenDeleteLastPermissionModal(collaboratorId, collaboratorType, code);
          } else if (
            profiles[userId] &&
            //@ts-expect-error Authority Schema issue "UserPublic": doesn't have 'is_admin' (not sure if should)
            (profiles[userId].is_superuser || profiles[userId].is_admin)
          ) {
            removePermission({
              code,
              user: collaboratorId,
              recursive,
              objectId,
              objectType: data.type,
            });
          } else if (
            code === 'remove_permission' &&
            collaboratorId === userId &&
            isLastRemovePermission(collaboratorId)
          ) {
            handleOpenRemoveRemoveModal(collaboratorType, code);
          } else if (
            code === 'owner' &&
            collaboratorId === userId &&
            !permissionsAux['owner'].locked
          ) {
            handleOpenRemoveOwnerModal(collaboratorType, code, collaboratorId);
          } else {
            removePermission({
              code,
              user: collaboratorId,
              recursive,
              objectId,
              objectType: data.type,
            });
          }
        } else {
          addPermission({
            code,
            user: collaboratorId,
            recursive,
            objectId,
            objectType: data.type,
          });
        }
      } else {
        if (permissionsAux[code].active) {
          if (isLastPermission(collaboratorId, collaboratorType)) {
            handleOpenDeleteLastPermissionModal(collaboratorId, collaboratorType, code);
          } else if (
            profiles[userId] &&
            //@ts-expect-error Authority Schema issue "UserPublic": doesn't have 'is_admin' (not sure if should)
            (profiles[userId].is_superuser || profiles[userId].is_admin)
          ) {
            removePermission({
              code,
              group: collaboratorId,
              recursive,
              objectId,
              objectType: data.type,
            });
          } else if (
            code === 'remove_permission' &&
            currentUserGroups[userId] &&
            currentUserGroups[userId].includes(collaboratorId) &&
            isMyLastGroupWithPermission('remove_permission') &&
            (!data.permissions.users[userId] ||
              (data.permissions.users[userId] &&
                !data.permissions.users[userId].includes('remove_permission')))
          ) {
            handleOpenRemoveRemoveModal(collaboratorType, code, collaboratorId);
          } else if (
            code !== 'owner' ||
            (data.permissions.users[userId] && data.permissions.users[userId].includes('owner')) ||
            !isMyLastGroupWithPermission('owner')
          ) {
            removePermission({
              code,
              group: collaboratorId,
              recursive,
              objectId,
              objectType: data.type,
            });
          } else if (
            currentUserGroups[userId] &&
            currentUserGroups[userId].includes(collaboratorId)
          ) {
            handleOpenRemoveOwnerModal(collaboratorType, code, collaboratorId);
          }
        } else {
          addPermission({
            code,
            group: collaboratorId,
            recursive,
            objectId,
            objectType: data.type,
          });
        }
      }
    }
  };

  const rowsData = useMemo<TableRow<RowData>[]>(() => {
    return (Object.keys(collaborators) as Array<keyof typeof collaborators>)
      .sort((a, b) => collaborators[b].order - collaborators[a].order)
      .map<TableRow<RowData>>((collaboratorId) => {
        const collaborator = collaborators[collaboratorId];

        return {
          ...collaborator,
          id: collaboratorId,
          suffix: {
            children: (
              <RemoveColumn collaborator={collaborator} objectId={objectId} recursive={recursive} />
            ),
          },
        };
      });
  }, [collaborators, objectId]);
  if (!data || rowsData.length <= 0) {
    return null;
  }

  return (
    <div style={{ margin: '2rem 0 0 0', width: '100%', height: '100%', overflow: 'auto' }}>
      <BaseTable
        id="PermissionsTable"
        height={'100%'}
        options={{ columns, data: rowsData }}
        maxHeight={maxHeight}
        testId={testId}
        identity="permissions"
        selectable={false}
        isLoading={false}
        loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
        renderFooter={() => null}
        columnPinning={{ left: ['name', 'roles'] }}
      />
    </div>
  );
};

export default Table;
